/**
 * OSPROD-BOOK: owner-facing management of shareable booking links.
 *
 * Lives at /calendar/booking-pages. Lists every link the user owns,
 * exposes inline create / edit / delete, and shows the public share
 * URL with a copy-to-clipboard chip so the user can hand it to a
 * customer in two clicks. The form supports per-weekday multi-range
 * availability so the user can carve out a lunch break inside a
 * 9-18 working day, plus a validity window (start/end date) and a
 * configurable slot duration + buffer between bookings.
 */

import { dialog } from '../services/dialog'
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Copy, Trash2, Pencil, Check, ExternalLink, Power } from 'lucide-react'
import AppLayout from '../components/layout/AppLayout'
import { copyToClipboard } from '../utils/clipboard'
import {
  bookingApi,
  type BookingLink,
  type BookingLinkInput,
  type BookingWeeklyAvailability,
  type BookingDayKey,
  type BookingAvailabilityRange,
  type BookingAppointment,
} from '../services/api'

const DAY_KEYS: BookingDayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const DAY_LABELS: Record<BookingDayKey, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
}

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120]

function defaultWeekly(): BookingWeeklyAvailability {
  // Mon–Fri 9-12 + 13-18 by default (with the lunch break carved
  // out the user explicitly mentioned). Weekend off.
  const weekday = [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '18:00' }]
  return {
    mon: weekday, tue: weekday, wed: weekday, thu: weekday, fri: weekday,
    sat: null, sun: null,
  }
}

function publicUrl(slug: string): string {
  return `${window.location.origin}/book/${slug}`
}

export default function BookingPagesPage() {
  const [links, setLinks] = useState<BookingLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<BookingLink | 'new' | null>(null)
  const [appointmentsFor, setAppointmentsFor] = useState<BookingLink | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const reload = async () => {
    setLoading(true)
    try {
      const res = await bookingApi.list()
      setLinks(res.data)
      setError(null)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load booking links')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { reload() }, [])

  // Calendar sidebar deep-links into this page with `?new=1` (open
  // the editor on a blank link) or `?edit=<id>` (open the editor on
  // an existing link). Wait until the list has loaded before
  // resolving `edit` so we have the row to hand into the form.
  useEffect(() => {
    if (loading) return
    if (searchParams.get('new') === '1') {
      setEditing('new')
      const next = new URLSearchParams(searchParams)
      next.delete('new')
      setSearchParams(next, { replace: true })
      return
    }
    const editId = searchParams.get('edit')
    if (editId) {
      const found = links.find((l) => l.id === editId)
      if (found) setEditing(found)
      const next = new URLSearchParams(searchParams)
      next.delete('edit')
      setSearchParams(next, { replace: true })
    }
  }, [loading, links, searchParams, setSearchParams])

  const handleDelete = async (link: BookingLink) => {
    if (!(await dialog.confirm({ message: `Delete booking link "${link.title}"? Existing appointments stay in your calendar but the public page goes 404.`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    try {
      await bookingApi.delete(link.id)
      reload()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete')
    }
  }

  const handleToggleActive = async (link: BookingLink) => {
    try {
      await bookingApi.update(link.id, { isActive: !link.isActive })
      reload()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update')
    }
  }

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto bg-background">
        <header className="sticky top-0 z-10 bg-background/85 backdrop-blur-md border-b border-border px-8 py-4 flex items-center gap-3">
          <h1 className="text-xl font-semibold text-foreground flex-1">Booking pages</h1>
          <Link
            to="/workspace/calendar"
            className="text-[12px] text-muted-foreground hover:text-foreground transition"
          >
            ← back to calendar
          </Link>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-[13px] font-medium text-primary-foreground hover:bg-primary/90 transition"
          >
            <Plus size={14} />
            New booking page
          </button>
        </header>

        <div className="px-8 py-6 max-w-4xl">
          {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
          {error && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2 mb-4">{error}</div>}
          {!loading && links.length === 0 && (
            <div className="text-center py-16">
              <div className="text-foreground text-lg font-medium mb-2">No booking pages yet</div>
              <p className="text-muted-foreground text-sm mb-4 max-w-md mx-auto">
                Create a booking page and share its link — clients will see only your free slots and pick one. Their booking lands on your calendar automatically.
              </p>
              <button
                type="button"
                onClick={() => setEditing('new')}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[13px] font-medium text-primary-foreground hover:bg-primary/90 transition"
              >
                <Plus size={14} />
                Create your first page
              </button>
            </div>
          )}

          <div className="space-y-3">
            {links.map((link) => (
              <BookingLinkRow
                key={link.id}
                link={link}
                onEdit={() => setEditing(link)}
                onDelete={() => handleDelete(link)}
                onToggleActive={() => handleToggleActive(link)}
                onShowAppointments={() => setAppointmentsFor(link)}
              />
            ))}
          </div>
        </div>
      </div>

      {editing && (
        <BookingLinkEditor
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}

      {appointmentsFor && (
        <AppointmentsModal
          link={appointmentsFor}
          onClose={() => setAppointmentsFor(null)}
        />
      )}
    </AppLayout>
  )
}

function BookingLinkRow({
  link, onEdit, onDelete, onToggleActive, onShowAppointments,
}: {
  link: BookingLink
  onEdit: () => void
  onDelete: () => void
  onToggleActive: () => void
  onShowAppointments: () => void
}) {
  const [copied, setCopied] = useState(false)
  const url = publicUrl(link.slug)
  const handleCopy = async () => {
    if (await copyToClipboard(url)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className={`rounded-xl border ${link.isActive ? 'border-border bg-card' : 'border-border bg-muted/30'} p-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`text-[15px] font-semibold ${link.isActive ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
              {link.title}
            </h3>
            {!link.isActive && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">disabled</span>
            )}
          </div>
          {link.description && (
            <p className="text-[12.5px] text-muted-foreground mb-2 line-clamp-2">{link.description}</p>
          )}
          <div className="flex items-center gap-3 text-[11.5px] text-muted-foreground flex-wrap">
            <span>{link.durationMinutes} min</span>
            {link.bufferMinutes > 0 && <span>{link.bufferMinutes} min buffer</span>}
            <span>·</span>
            <button
              type="button"
              onClick={onShowAppointments}
              className="hover:text-foreground transition underline-offset-2 hover:underline"
            >
              {link._count?.appointments ?? 0} bookings
            </button>
            <span>·</span>
            <span>{link.timezone}</span>
            {link.endsAt && (
              <>
                <span>·</span>
                <span>expires {new Date(link.endsAt).toLocaleDateString()}</span>
              </>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 text-[12px] bg-muted text-foreground rounded px-2 py-1 truncate">{url}</code>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition px-2 py-1 rounded hover:bg-muted"
              title="Copy link"
            >
              {copied ? <><Check size={12} className="text-green-500" /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition px-2 py-1 rounded hover:bg-muted"
              title="Open public page"
            >
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggleActive}
            className={`p-2 rounded transition ${link.isActive ? 'text-muted-foreground hover:text-foreground hover:bg-muted' : 'text-amber-500 hover:bg-amber-500/10'}`}
            title={link.isActive ? 'Disable' : 'Enable'}
          >
            <Power size={14} />
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-2 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

function BookingLinkEditor({
  existing, onClose, onSaved,
}: {
  existing: BookingLink | null
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = useState(existing?.title ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [slug, setSlug] = useState(existing?.slug ?? '')
  const [durationMinutes, setDurationMinutes] = useState<number>(existing?.durationMinutes ?? 30)
  const [bufferMinutes, setBufferMinutes] = useState<number>(existing?.bufferMinutes ?? 0)
  const [startsAt, setStartsAt] = useState(existing?.startsAt?.slice(0, 10) ?? '')
  const [endsAt, setEndsAt] = useState(existing?.endsAt?.slice(0, 10) ?? '')
  const [weekly, setWeekly] = useState<BookingWeeklyAvailability>(
    existing?.weeklyAvailability ?? defaultWeekly(),
  )
  // OSPROD-BOOK followup: per-link timezone. New links default to
  // the owner's browser zone — the most likely intent — but they
  // can switch (handy for ops people scheduling on behalf of
  // someone in another office).
  const [timezone, setTimezone] = useState<string>(
    existing?.timezone ?? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload: BookingLinkInput = {
        title: title.trim(),
        description: description.trim() || null,
        slug: slug.trim() || undefined,
        durationMinutes,
        bufferMinutes,
        startsAt: startsAt ? new Date(startsAt + 'T00:00:00').toISOString() : null,
        endsAt: endsAt ? new Date(endsAt + 'T23:59:59').toISOString() : null,
        weeklyAvailability: weekly,
        timezone,
      }
      if (existing) {
        await bookingApi.update(existing.id, payload)
      } else {
        await bookingApi.create(payload)
      }
      onSaved()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-card rounded-xl shadow-2xl border border-border w-[640px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-foreground font-semibold">
            {existing ? 'Edit booking page' : 'New booking page'}
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">×</button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          {error && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>}

          <Field label="Title">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="30-min discovery call"
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
            />
          </Field>
          <Field label="Description (shown on the public page)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What the customer should know before booking"
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </Field>
          <Field label="Slug (URL piece)" hint="Letters/numbers/hyphens. Leave blank to auto-generate.">
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="dmitriy-30min"
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration">
              <select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
              >
                {DURATION_PRESETS.map((d) => (
                  <option key={d} value={d}>{d} min</option>
                ))}
              </select>
            </Field>
            <Field label="Buffer between bookings" hint="Cushion around busy times.">
              <select
                value={bufferMinutes}
                onChange={(e) => setBufferMinutes(Number(e.target.value))}
                className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
              >
                {[0, 5, 10, 15, 30].map((d) => (
                  <option key={d} value={d}>{d === 0 ? 'No buffer' : `${d} min`}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Bookable from" hint="Earliest date a client can pick. Empty = today.">
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>
            <Field label="Link expires on" hint="After this date the page goes 404.">
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>
          </div>

          <Field
            label="Timezone"
            hint="Working hours below are interpreted in this zone. Customers see slots in their own local time."
          >
            <TimezonePicker value={timezone} onChange={setTimezone} />
          </Field>

          <div>
            <div className="text-[11px] uppercase text-muted-foreground tracking-wide mb-2">Weekly availability</div>
            <p className="text-[11.5px] text-muted-foreground mb-3">
              Pick the hours you're free for these meetings. Add a second range to carve out a lunch break.
            </p>
            <div className="space-y-2">
              {DAY_KEYS.map((d) => (
                <DayAvailabilityRow
                  key={d}
                  dayKey={d}
                  ranges={weekly[d]}
                  onChange={(next) => setWeekly({ ...weekly, [d]: next })}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-muted-foreground hover:text-foreground transition">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="px-4 py-1.5 text-[13px] bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Saving…' : existing ? 'Save changes' : 'Create page'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DayAvailabilityRow({
  dayKey, ranges, onChange,
}: {
  dayKey: BookingDayKey
  ranges: BookingAvailabilityRange[] | null
  onChange: (next: BookingAvailabilityRange[] | null) => void
}) {
  const enabled = ranges !== null && ranges.length > 0
  return (
    <div className="flex items-start gap-3 py-1.5">
      <label className="w-20 flex items-center gap-2 pt-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? [{ start: '09:00', end: '17:00' }] : null)}
          className="accent-primary"
        />
        <span className="text-[13px] text-foreground">{DAY_LABELS[dayKey]}</span>
      </label>
      <div className="flex-1 space-y-1.5">
        {!enabled && (
          <span className="text-[12px] text-muted-foreground italic pt-1.5 inline-block">Not bookable</span>
        )}
        {enabled && ranges!.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="time"
              value={r.start}
              onChange={(e) => {
                const next = [...ranges!]
                next[i] = { ...next[i], start: e.target.value }
                onChange(next)
              }}
              className="bg-muted text-foreground rounded px-2 py-1 text-[12.5px] outline-none focus:ring-2 focus:ring-primary"
            />
            <span className="text-muted-foreground">–</span>
            <input
              type="time"
              value={r.end}
              onChange={(e) => {
                const next = [...ranges!]
                next[i] = { ...next[i], end: e.target.value }
                onChange(next)
              }}
              className="bg-muted text-foreground rounded px-2 py-1 text-[12.5px] outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => {
                const next = ranges!.filter((_, idx) => idx !== i)
                onChange(next.length === 0 ? null : next)
              }}
              className="text-muted-foreground hover:text-destructive p-1"
              title="Remove range"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {enabled && (
          <button
            type="button"
            onClick={() => onChange([...(ranges ?? []), { start: '13:00', end: '18:00' }])}
            className="text-[11.5px] text-primary hover:underline"
          >
            + add another range
          </button>
        )}
      </div>
    </div>
  )
}

// Modern Chromium / Node 18+ expose Intl.supportedValuesOf so we
// can produce a real IANA list for the dropdown. Older runtimes
// fall through to a curated short list — covers the common
// timezones this product's users sit in.
const FALLBACK_TZS = [
  'UTC', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'Europe/Moscow', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore',
  'Asia/Tokyo', 'Australia/Sydney',
  'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Sao_Paulo',
]

function timezoneOptions(): string[] {
  try {
    const sup = (Intl as any).supportedValuesOf
    if (typeof sup === 'function') return sup('timeZone')
  } catch { /* fall through */ }
  return FALLBACK_TZS
}

function TimezonePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const options = useMemo(() => timezoneOptions(), [])
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
    >
      {options.map((tz) => (
        <option key={tz} value={tz}>{tz}</option>
      ))}
    </select>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-muted-foreground tracking-wide mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-muted-foreground/80 mt-1">{hint}</div>}
    </div>
  )
}

function AppointmentsModal({ link, onClose }: { link: BookingLink; onClose: () => void }) {
  const [appointments, setAppointments] = useState<BookingAppointment[] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    bookingApi.appointments(link.id)
      .then((res) => setAppointments(res.data))
      .finally(() => setLoading(false))
  }, [link.id])

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-card rounded-xl shadow-2xl border border-border w-[560px] max-w-[95vw] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-foreground font-semibold">Bookings on “{link.title}”</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">×</button>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
          {!loading && appointments?.length === 0 && (
            <div className="text-muted-foreground text-sm py-6 text-center">No bookings yet.</div>
          )}
          <div className="space-y-2">
            {appointments?.map((a) => (
              <div key={a.id} className="rounded border border-border bg-muted/40 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium text-foreground">{a.customerName}</span>
                  <span className={`text-[10px] uppercase tracking-wide ${a.status === 'CONFIRMED' ? 'text-green-500' : 'text-muted-foreground'}`}>{a.status}</span>
                </div>
                <div className="text-[11.5px] text-muted-foreground">{a.customerEmail}</div>
                <div className="text-[12px] text-foreground mt-1">
                  {new Date(a.startsAt).toLocaleString()}
                </div>
                {a.notes && <div className="text-[12px] text-muted-foreground mt-1 whitespace-pre-line">{a.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
